import { createSelector } from 'reselect'

import { selectActualFeatureFlags } from 'containers/App/selectors'
import { CAMPAIGN_TYPES_VALUES, DELIVERY } from 'containers/AwarenessCenter/constants'

import { adaptCampaignHealth } from 'components/pages/awareness-center/CampaignReport/adapters/campaignHealthAdapter'
import { adaptCampaignInfo } from 'components/pages/awareness-center/CampaignReport/adapters/campaignInfoAdapter'
import { adaptCampaignStatusCounts } from 'components/pages/awareness-center/CampaignReport/adapters/campaignStatusCountsAdapter'
import { adaptCampaignStatusOverTime } from 'components/pages/awareness-center/CampaignReport/adapters/campaignStatusOverTimeAdapter'
import { adaptSimulationCampaignInfo } from 'components/pages/awareness-center/CampaignReport/adapters/simulationCampaignInfoAdapter'
import { adaptSimulationDepartmentOutcomes } from 'components/pages/awareness-center/CampaignReport/adapters/simulationDepartmentOutcomesAdapter'
import { adaptSimulationFunnel } from 'components/pages/awareness-center/CampaignReport/adapters/simulationFunnelAdapter'
import { adaptSimulationOverview } from 'components/pages/awareness-center/CampaignReport/adapters/simulationOverviewAdapter'

import { adaptCampaignCompletionProgress } from '../../../../components/pages/awareness-center/CampaignReport/adapters/campaignCompletionProgressAdapter'
import { adaptCampaignFinalResults } from '../../../../components/pages/awareness-center/CampaignReport/adapters/campaignFinalResultsAdapter'
import { adaptCampaignParticipants } from '../../../../components/pages/awareness-center/CampaignReport/adapters/campaignParticipantsAdapter'

import { CAMPAIGN_RESULT_STATUSES, TRAINING_STATUS_OPTIONS } from './constants'

const selectData = (state) => state.awarenessCenter.campaignReport.data
const selectLoader = (state) => state.awarenessCenter.campaignReport.loader
const selectTableLoader = (state) => state.awarenessCenter.campaignReport.tableLoader
const selectDuplicateCampaignLoader = (state) =>
  state.awarenessCenter.campaignReport.duplicateCampaignLoader
const selectStopCampaignLoader = (state) => state.awarenessCenter.campaignReport.stopCampaignLoader
const selectCampaignStopped = (state) => state.awarenessCenter.campaignReport.campaignStopped
const selectError = (state) => state.awarenessCenter.campaignReport.error
const selectSegment = (state) => state.awarenessCenter.campaignReport.segment
const selectEmailPreviewLoader = (state) => state.awarenessCenter.campaignReport.emailPreviewLoader
const selectEmailPreviewError = (state) => state.awarenessCenter.campaignReport.emailPreviewError
const selectEmailPreview = (state) => state.awarenessCenter.campaignReport.emailPreview
const selectFilters = (state) => state.awarenessCenter.campaignReport.filters
const selectEmailDetails = (state) => state.awarenessCenter.campaignReport.emailDetails
const selectSummaryData = (state) => state.awarenessCenter.campaignReport.summaryData
const selectCreatedCampaignId = (state) => state.awarenessCenter.campaignReport.createdCampaignId
const selectReportParticipantsData = (state) =>
  state.awarenessCenter.campaignReport.reportParticipantsData
const selectTrainingResultsModalStatus = (state) =>
  state.awarenessCenter.campaignReport.trainingResultsModalStatus
const selectTrainingResultsPostponedDate = (state) =>
  state.awarenessCenter.campaignReport.trainingResultsPostponedDate
const selectTrainingResultsModalLoader = (state) =>
  state.awarenessCenter.campaignReport.trainingResultsModalLoader
const selectCampaignHealthState = (state) => state.awarenessCenter.campaignReport.health
const selectCampaignStatusCountsState = (state) => state.awarenessCenter.campaignReport.statusCounts
const selectCampaignStatusOverTimeState = (state) =>
  state.awarenessCenter.campaignReport.statusOverTime
const selectCampaignCompletionProgressState = (state) =>
  state.awarenessCenter.campaignReport.completionProgress
const selectCampaignInfoState = (state) => state.awarenessCenter.campaignReport.info
const selectSimulationOverviewState = (state) =>
  state.awarenessCenter.campaignReport.simulationOverview
const selectSimulationFunnelState = (state) => state.awarenessCenter.campaignReport.simulationFunnel
const selectDepartmentOutcomesState = (state) =>
  state.awarenessCenter.campaignReport.departmentOutcomes
const selectCampaignFinalResultsState = (state) => state.awarenessCenter.campaignReport.finalResults
const selectCampaignParticipantsState = (state) => state.awarenessCenter.campaignReport.participants
const selectAssignmentResendsState = (state) =>
  state.awarenessCenter.campaignReport.assignmentResends
const selectExtendCollectionPeriodState = (state) =>
  state.awarenessCenter.campaignReport.extendCollectionPeriod
const selectResetParticipantDeadlinesState = (state) =>
  state.awarenessCenter.campaignReport.resetParticipantDeadlines
const selectExtendParticipantDeadlinesState = (state) =>
  state.awarenessCenter.campaignReport.extendParticipantDeadlines
const selectParticipantExclusionsState = (state) =>
  state.awarenessCenter.campaignReport.participantExclusions
const selectProgressResetsState = (state) => state.awarenessCenter.campaignReport.progressResets
const selectManualCompletionsState = (state) =>
  state.awarenessCenter.campaignReport.manualCompletions
const selectManualCompletionRevertsState = (state) =>
  state.awarenessCenter.campaignReport.manualCompletionReverts
const selectReminderPausesState = (state) => state.awarenessCenter.campaignReport.reminderPauses
const selectReminderResumesState = (state) => state.awarenessCenter.campaignReport.reminderResumes
const selectNotifyManagersState = (state) => state.awarenessCenter.campaignReport.notifyManagers
const selectSendRemindersState = (state) => state.awarenessCenter.campaignReport.sendReminders
const selectParticipantsCsvState = (state) => state.awarenessCenter.campaignReport.participantsCsv
const selectStatusRemindersState = (state) => state.awarenessCenter.campaignReport.statusReminders

const asWidgetCard = (slice, adapt) => {
  const idle = slice.data === null && !slice.loader && !slice.error

  return {
    loading: slice.loader || idle,
    error: slice.error,
    ...adapt(slice.data),
  }
}

export const selectCampaignHealthCard = createSelector(selectCampaignHealthState, (health) =>
  asWidgetCard(health, adaptCampaignHealth)
)

export const selectCampaignStatusCountsCard = createSelector(
  selectCampaignStatusCountsState,
  (statusCounts) => asWidgetCard(statusCounts, adaptCampaignStatusCounts)
)

export const selectCampaignStatusOverTimeChart = createSelector(
  selectCampaignStatusOverTimeState,
  (statusOverTime) => asWidgetCard(statusOverTime, adaptCampaignStatusOverTime)
)

export const selectCampaignCompletionProgressCard = createSelector(
  selectCampaignCompletionProgressState,
  (completionProgress) => asWidgetCard(completionProgress, adaptCampaignCompletionProgress)
)

export const selectCampaignInfoCard = createSelector(selectCampaignInfoState, (info) =>
  asWidgetCard(info, adaptCampaignInfo)
)

export const selectSimulationCampaignInfoCard = createSelector(selectCampaignInfoState, (info) =>
  asWidgetCard(info, adaptSimulationCampaignInfo)
)

export const selectSimulationOverviewCard = createSelector(
  selectSimulationOverviewState,
  (overview) => asWidgetCard(overview, adaptSimulationOverview)
)

export const selectSimulationFunnelCard = createSelector(selectSimulationFunnelState, (funnel) =>
  asWidgetCard(funnel, adaptSimulationFunnel)
)

export const selectSimulationDepartmentOutcomesCard = createSelector(
  selectDepartmentOutcomesState,
  (departments) => asWidgetCard(departments, adaptSimulationDepartmentOutcomes)
)

export const selectCampaignFinalResultsCard = createSelector(
  selectCampaignFinalResultsState,
  (finalResults) => asWidgetCard(finalResults, adaptCampaignFinalResults)
)

export const selectCampaignParticipantsCard = createSelector(
  selectCampaignParticipantsState,
  (participants) => asWidgetCard(participants, adaptCampaignParticipants)
)

export const selectTemplateOptions = createSelector(
  selectReportParticipantsData,
  (participantsData) =>
    participantsData.meta.scenarios.map((template) => ({
      label: template,
      value: template,
    }))
)

export const selectSimulationResultOptions = createSelector(selectData, (data) => {
  let options = TRAINING_STATUS_OPTIONS.filter(
    (e) =>
      e.value !== CAMPAIGN_RESULT_STATUSES.POSTPONED &&
      e.value !== CAMPAIGN_RESULT_STATUSES.TRAINED &&
      e.value !== CAMPAIGN_RESULT_STATUSES.NOT_TRAINED &&
      e.value !== CAMPAIGN_RESULT_STATUSES.STARTED_NOT_COMPLETED &&
      e.value !== CAMPAIGN_RESULT_STATUSES.NOT_STARTED &&
      e.value !== CAMPAIGN_RESULT_STATUSES.MANUALLY_TRAINED &&
      e.value !== CAMPAIGN_RESULT_STATUSES.NOT_AVAILABLE &&
      e.value !== CAMPAIGN_RESULT_STATUSES.TRAINING_BLANK_LEGACY &&
      e.value !== CAMPAIGN_RESULT_STATUSES.SCHEDULED_TO_SEND
  )

  if (data.delivery_type === DELIVERY.SMS) {
    options = options.filter((e) => e.value !== CAMPAIGN_RESULT_STATUSES.REPORTED)
  }

  return options
})

export const selectTrainingResultOptions = createSelector(
  selectData,
  selectActualFeatureFlags,
  selectData,
  (data, ff, campaignData) => {
    const isTriggerBasedCampaign = campaignData.triggerBasedCampaignData

    if (data.flow_type === CAMPAIGN_TYPES_VALUES.TRAINING) {
      // For training campaigns the legacy NOT_TRAINED bucket is replaced
      // by the two finer-grained segments. We keep filtering both
      // STARTED_NOT_COMPLETED and NOT_STARTED out of other branches so they
      // can only ever be selected here.
      let options = TRAINING_STATUS_OPTIONS.filter(
        (e) =>
          e.value !== CAMPAIGN_RESULT_STATUSES.CLICKED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.REPORTED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.ENTERED_DATA &&
          e.value !== CAMPAIGN_RESULT_STATUSES.NOT_AVAILABLE &&
          e.value !== CAMPAIGN_RESULT_STATUSES.NOT_TRAINED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.TRAINING_BLANK_LEGACY
      )

      if (!isTriggerBasedCampaign) {
        options = options.filter((e) => e.value !== CAMPAIGN_RESULT_STATUSES.SCHEDULED_TO_SEND)
      }

      return options
    }

    if (data.flow_type === CAMPAIGN_TYPES_VALUES.SPEAR_PHISHING) {
      return TRAINING_STATUS_OPTIONS.filter(
        (e) =>
          e.value !== CAMPAIGN_RESULT_STATUSES.CLICKED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.REPORTED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.ENTERED_DATA &&
          e.value !== CAMPAIGN_RESULT_STATUSES.OPENED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.UNOPENED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.NOT_DELIVERED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.STARTED_NOT_COMPLETED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.NOT_STARTED &&
          e.value !== CAMPAIGN_RESULT_STATUSES.TRAINING_BLANK_LEGACY &&
          e.value !== CAMPAIGN_RESULT_STATUSES.SCHEDULED_TO_SEND
      )
    }

    let options = TRAINING_STATUS_OPTIONS.filter(
      (e) =>
        e.value !== CAMPAIGN_RESULT_STATUSES.CLICKED &&
        e.value !== CAMPAIGN_RESULT_STATUSES.REPORTED &&
        e.value !== CAMPAIGN_RESULT_STATUSES.ENTERED_DATA &&
        e.value !== CAMPAIGN_RESULT_STATUSES.OPENED &&
        e.value !== CAMPAIGN_RESULT_STATUSES.UNOPENED &&
        e.value !== CAMPAIGN_RESULT_STATUSES.NOT_DELIVERED &&
        e.value !== CAMPAIGN_RESULT_STATUSES.STARTED_NOT_COMPLETED &&
        e.value !== CAMPAIGN_RESULT_STATUSES.NOT_STARTED
    )

    if (!ff.CUSTOM_PARTICIPANT_FILTERS_LEGACY) {
      options = options.filter((e) => e.value !== CAMPAIGN_RESULT_STATUSES.TRAINING_BLANK_LEGACY)
    }

    if (!isTriggerBasedCampaign) {
      options = options.filter((e) => e.value !== CAMPAIGN_RESULT_STATUSES.SCHEDULED_TO_SEND)
    }

    return options
  }
)

const selectDepartmentsOptions = createSelector(selectReportParticipantsData, (data) =>
  data.meta.departments.map((department) => ({
    label: department,
    value: department,
  }))
)

const selectSelectionData = (state) => state.awarenessCenter.campaignReport.selectionData

export {
  selectData,
  selectLoader,
  selectTableLoader,
  selectDuplicateCampaignLoader,
  selectStopCampaignLoader,
  selectCampaignStopped,
  selectError,
  selectSegment,
  selectEmailPreviewLoader,
  selectEmailPreviewError,
  selectEmailPreview,
  selectReportParticipantsData,
  selectFilters,
  selectDepartmentsOptions,
  selectEmailDetails,
  selectSummaryData,
  selectCreatedCampaignId,
  selectSelectionData,
  selectTrainingResultsModalStatus,
  selectTrainingResultsPostponedDate,
  selectTrainingResultsModalLoader,
  selectCampaignHealthState,
  selectCampaignStatusCountsState,
  selectCampaignStatusOverTimeState,
  selectCampaignCompletionProgressState,
  selectCampaignInfoState,
  selectSimulationOverviewState,
  selectSimulationFunnelState,
  selectDepartmentOutcomesState,
  selectCampaignFinalResultsState,
  selectCampaignParticipantsState,
  selectAssignmentResendsState,
  selectExtendCollectionPeriodState,
  selectResetParticipantDeadlinesState,
  selectExtendParticipantDeadlinesState,
  selectParticipantExclusionsState,
  selectProgressResetsState,
  selectManualCompletionsState,
  selectManualCompletionRevertsState,
  selectReminderPausesState,
  selectReminderResumesState,
  selectNotifyManagersState,
  selectSendRemindersState,
  selectParticipantsCsvState,
  selectStatusRemindersState,
}
